/**
 * TLR header behaviour: scroll state, mobile overlay, desktop dropdowns.
 *
 * Kept out of custom-scripts.js so the previous theme's orphaned code can
 * be deleted wholesale later without picking this back out of it.
 *
 * Syntax note: gulp-babel runs without any preset configured, so nothing
 * here is actually transpiled and uglify-js sees this source as written.
 * Arrow functions, const/let and template literals are fine; optional
 * chaining (?.) and nullish coalescing (??) are NOT — they will break the
 * build.
 */

/* =========================================================
   SCROLL STATE
   ========================================================= */
(function () {
  const header = document.getElementById("siteHeader");
  const netBar = document.getElementById("netBar");
  if (!header) return;
  let ticking = false;
  function apply() {
    const scrolled = (window.pageYOffset || document.documentElement.scrollTop) > 60;
    header.classList.toggle("is-scrolled", scrolled);
    if (netBar) netBar.classList.toggle("is-scrolled", scrolled);
    ticking = false;
  }
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(apply);
  }
  window.addEventListener("scroll", onScroll, {
    passive: true
  });
  apply(); // reloading mid-page should not start in the transparent state
})();

/* =========================================================
   MOBILE OVERLAY
   ========================================================= */
(function () {
  const burger = document.getElementById("burger");
  const menu = document.getElementById("mobileMenu");
  if (!burger || !menu) return;

  // Anything focusable that is actually reachable. Collapsed accordion panels
  // are zero-height but not hidden, so their links would otherwise be in the
  // tab order while invisible.
  const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';
  function focusable() {
    return Array.prototype.filter.call(menu.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0;
    });
  }
  function setOpen(open) {
    burger.setAttribute("aria-expanded", open ? "true" : "false");
    burger.setAttribute("aria-label", open ? "Close menu" : "Open menu");
    menu.setAttribute("aria-hidden", open ? "false" : "true");
    menu.classList.toggle("is-open", open);
    document.body.classList.toggle("menu-open", open);

    // The overlay covers the viewport, but the page behind it stays in the
    // tab order — without moving focus in and holding it there, tabbing
    // walks off into content the user cannot see.
    if (open) {
      const first = focusable()[0];
      if (first) first.focus();
    }
  }

  // Focus trap. Kept on the menu itself rather than marking the rest of the
  // page inert, because the header and the overlay are siblings inside
  // #page and there is no single container to disable.
  menu.addEventListener("keydown", function (e) {
    if (e.key !== "Tab" || !menu.classList.contains("is-open")) return;
    const items = focusable();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });
  burger.addEventListener("click", function () {
    setOpen(burger.getAttribute("aria-expanded") !== "true");
  });
  const closeBtn = document.getElementById("mmClose");
  if (closeBtn) {
    closeBtn.addEventListener("click", function () {
      setOpen(false);
      burger.focus();
    });
  }

  // Any navigation inside the overlay should dismiss it.
  const links = menu.querySelectorAll("[data-mm]");
  for (let i = 0; i < links.length; i++) {
    links[i].addEventListener("click", function () {
      setOpen(false);
    });
  }
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && menu.classList.contains("is-open")) {
      setOpen(false);
      burger.focus();
    }
  });

  // Accordions
  const triggers = menu.querySelectorAll(".mm-acc-trigger");
  for (let i = 0; i < triggers.length; i++) {
    triggers[i].addEventListener("click", function () {
      const acc = this.closest(".mm-acc");
      if (!acc) return;
      const open = acc.classList.toggle("open");
      this.setAttribute("aria-expanded", open ? "true" : "false");
    });
  }
})();

/* =========================================================
   DESKTOP DROPDOWNS

   The prototype opened panels with :hover and :focus-within only, and
   never updated aria-expanded. That leaves the panels unusable on touch
   above the 1200px burger breakpoint — a landscape tablet gets the desktop
   nav with hover-only dropdowns — and unreportable to screen readers.

   The CSS still opens on :focus-within so the menu degrades gracefully
   without JS. That is why closing needs an explicit `.is-closed` escape
   hatch: while focus is inside the item, removing `.is-open` alone would
   not close anything.
   ========================================================= */
(function () {
  const items = document.querySelectorAll(".nav-item");
  if (!items.length) return;
  const all = Array.prototype.slice.call(items);
  function trigger(item) {
    return item.querySelector(".nav-trigger");
  }
  function isOpen(item) {
    if (item.classList.contains("is-closed")) return false;
    return item.classList.contains("is-open") || item.contains(document.activeElement);
  }
  function setExpanded(item, expanded) {
    const btn = trigger(item);
    if (btn) btn.setAttribute("aria-expanded", expanded ? "true" : "false");
  }
  function close(item) {
    item.classList.remove("is-open");
    item.classList.add("is-closed");
    setExpanded(item, false);
  }
  function open(item) {
    item.classList.remove("is-closed");
    item.classList.add("is-open");
    setExpanded(item, true);
  }

  // Back to the neutral state, where hover and focus-within decide.
  function reset(item) {
    item.classList.remove("is-open", "is-closed");
    setExpanded(item, false);
  }
  function closeAll(except) {
    all.forEach(function (item) {
      if (item !== except) reset(item);
    });
  }
  all.forEach(function (item) {
    const btn = trigger(item);
    if (!btn) return;
    btn.addEventListener("click", function (e) {
      e.preventDefault();
      const wasOpen = isOpen(item);
      closeAll(item);
      if (wasOpen) {
        close(item);
      } else {
        open(item);
      }
    });

    // Keep aria in sync when the panel is reached by keyboard, where the
    // CSS opens it via :focus-within without any click.
    item.addEventListener("focusin", function () {
      if (!item.classList.contains("is-closed")) setExpanded(item, true);
    });
    item.addEventListener("focusout", function (e) {
      if (!item.contains(e.relatedTarget)) reset(item);
    });
  });
  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape") return;
    const active = document.activeElement;
    all.forEach(function (item) {
      if (item.contains(active)) {
        close(item);
        const btn = trigger(item);
        if (btn) btn.focus(); // keep the user where they were
      } else {
        reset(item);
      }
    });
  });
  document.addEventListener("click", function (e) {
    if (!e.target.closest(".nav-item")) closeAll(null);
  });
})();